'use client';

import Image from 'next/image';
import Link from 'next/link';

import { Banknote, CreditCard, Package, Users, Wallet } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@kit/ui/button';
import { Card, CardContent } from '@kit/ui/card';
import { CurrencyIcon } from '@kit/ui/currency-icon';
import { RadioGroup, RadioGroupItem } from '@kit/ui/radio-group';

import { getWalletPaymentAvailability } from '../_lib/payment/wallet-availability';

export type PaymentMethod =
  | 'cash'
  | 'complimentary'
  | 'package'
  | 'wallet'
  | 'applepay'
  | 'card'
  | 'split';

interface PaymentMethodSelectorProps {
  availableCredits: number;
  hasMatchingDuration: boolean;
  // Wallet tender: the customer's current wallet balance + whether the tenant
  // has the wallet feature on. The option remains visible when enabled and is
  // disabled when the balance cannot cover the full price.
  walletBalance?: number;
  walletEnabled?: boolean;
  hideWalletPayment?: boolean;
  // True when the customer holds a matching credit that was hidden ONLY because
  // the selected date is outside its attend-by package's validity. Drives a
  // one-line hint so the vanished package option isn't a silent mystery.
  creditsHiddenForDate?: boolean;
  showPackageParticipantHint?: boolean;
  bookingPrice: number | null;
  /** Original (pre-discount) price. When provided and greater than `bookingPrice`,
      shown struck-through alongside the discounted total. */
  originalPrice?: number | null;
  selectedMethod: PaymentMethod;
  onMethodChange: (method: PaymentMethod) => void;
  disabled?: boolean;
  isGuest?: boolean;
  isApplePayAvailable?: boolean;
  // Split payment props
  splitPayerCount?: number;
  onSplitPayerCountChange?: (count: number) => void;
  // When set, the member's own share is covered by their membership perk: the
  // OTHER players each pay `othersPay`, the member pays 0, and the booking
  // confirms once they pay. Mirrors the server's comp so the UI doesn't promise
  // a per-share that differs from what gets created.
  splitComp?: { share: number; othersPay: number } | null;
  // Cap split options (e.g., trainer per-person bookings limit to numberOfParticipants)
  maxSplitPayers?: number;
  // Auto-select this split count when split payment is chosen
  autoSelectSplitCount?: number;
  // Hide split payment option (e.g., for class bookings)
  hideSplitPayment?: boolean;
  // Hide cash payment option (controlled by business settings)
  hideCashPayment?: boolean;
  // Hide card and Apple Pay options (e.g., when class_package_card is false)
  hideCardPayment?: boolean;
}

export function PaymentMethodSelector({
  availableCredits,
  hasMatchingDuration,
  walletBalance = 0,
  walletEnabled = false,
  hideWalletPayment = false,
  creditsHiddenForDate = false,
  showPackageParticipantHint = false,
  bookingPrice,
  originalPrice,
  selectedMethod,
  onMethodChange,
  disabled = false,
  isGuest = false,
  isApplePayAvailable = true,
  splitPayerCount = 4,
  onSplitPayerCountChange,
  splitComp = null,
  maxSplitPayers,
  autoSelectSplitCount,
  hideSplitPayment = false,
  hideCashPayment = false,
  hideCardPayment = false,
}: PaymentMethodSelectorProps) {
  const { t, i18n } = useTranslation('bookings');
  const isRTL = i18n.language === 'ar';

  // Show package option only if user has credits with matching duration and is not a guest
  const showPackageOption =
    !isGuest && availableCredits > 0 && hasMatchingDuration;

  // Wallet is offered only when the customer actually has funds; a short
  // (but positive) balance stays visible and disabled with the live figure.
  const walletAvailability = getWalletPaymentAvailability({
    isGuest,
    isHidden: hideWalletPayment,
    isEnabled: walletEnabled,
    bookingPrice,
    walletBalance,
  });

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
      <Card>
        <CardContent className="p-2 md:p-6">
          <div className="space-y-4">
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-primary text-lg font-semibold lg:text-xl">
                {t('paymentMethod')}
              </h3>
              {bookingPrice !== null && (
                <div className="flex items-center gap-2 text-lg font-semibold">
                  <span className="text-muted-foreground text-sm font-normal">
                    {t('total')}
                  </span>
                  {showPackageOption && selectedMethod === 'package' ? (
                    <>
                      <span className="text-green-600">{t('credits')}</span>
                      <span
                        dir="ltr"
                        className="text-muted-foreground inline-flex items-center gap-1"
                      >
                        <span style={{ textDecoration: 'line-through' }}>
                          <CurrencyIcon className="inline h-4 w-4" />
                          {bookingPrice.toFixed(2)}
                        </span>
                      </span>
                    </>
                  ) : (
                    <span dir="ltr" className="inline-flex items-center gap-2">
                      {originalPrice != null &&
                        originalPrice > bookingPrice && (
                          <span className="text-muted-foreground text-sm font-normal line-through">
                            {originalPrice.toFixed(2)}
                          </span>
                        )}
                      <span className="inline-flex items-center gap-1">
                        <CurrencyIcon className="h-4 w-4" />
                        <span>{bookingPrice.toFixed(2)}</span>
                      </span>
                    </span>
                  )}
                </div>
              )}
            </div>

            {/* Attend-by hint: a matching credit exists but is hidden because
                the chosen date is past its package's validity. */}
            {!showPackageOption && creditsHiddenForDate && (
              <p className="text-muted-foreground text-xs">
                {t('packageAttendByDateHint')}
              </p>
            )}

            {!isGuest && showPackageParticipantHint && (
              <p className="text-muted-foreground text-xs">
                <Trans i18nKey="bookings:packageParticipantCountHint" />
              </p>
            )}

            <RadioGroup
              value={selectedMethod}
              onValueChange={(value) => {
                onMethodChange(value as PaymentMethod);
                if (value === 'split' && autoSelectSplitCount) {
                  onSplitPayerCountChange?.(autoSelectSplitCount);
                }
              }}
              disabled={disabled}
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <div className="space-y-3">
                {/* Package Credit Option - First if available */}
                {showPackageOption && (
                  <label
                    data-native-selection-row
                    htmlFor="package"
                    className={`flex cursor-pointer items-center rounded-lg border p-4 transition-colors ${
                      selectedMethod === 'package'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="package" id="package" />
                      <div className="flex items-center gap-2">
                        <Package className="text-primary h-5 w-5 shrink-0" />
                        <div>
                          <p className="font-medium">{t('usePackageCredit')}</p>
                          <p className="text-muted-foreground text-sm">
                            {t('remainingCredits', { count: availableCredits })}
                          </p>
                        </div>
                      </div>
                    </div>
                  </label>
                )}

                {/* Wallet Option - visible when enabled; disabled if underfunded */}
                {walletAvailability.visible && (
                  <label
                    data-native-selection-row
                    htmlFor="wallet"
                    aria-disabled={disabled || !walletAvailability.canPay}
                    data-test="wallet-payment-option"
                    className={`flex cursor-pointer items-center rounded-lg border p-4 transition-colors ${
                      selectedMethod === 'wallet'
                        ? 'border-primary bg-primary/5'
                        : walletAvailability.canPay
                          ? 'border-border hover:bg-muted/50'
                          : 'border-border bg-muted/40'
                    } ${
                      disabled || !walletAvailability.canPay
                        ? 'cursor-not-allowed'
                        : ''
                    } ${disabled ? 'opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem
                        value="wallet"
                        id="wallet"
                        disabled={!walletAvailability.canPay}
                        aria-describedby={
                          walletAvailability.canPay
                            ? undefined
                            : 'wallet-insufficient-balance'
                        }
                        data-test="wallet-payment-radio"
                      />
                      <div className="flex items-center gap-2">
                        <Wallet className="h-5 w-5 shrink-0 text-emerald-600" />
                        <div>
                          <p className="font-medium">
                            {t('payWithWallet', {
                              defaultValue: 'Pay with Wallet',
                            })}
                          </p>
                          <p className="text-muted-foreground text-sm">
                            <span className="inline-flex items-baseline gap-1">
                              {t('walletBalanceLabel', {
                                defaultValue: 'Current balance',
                              })}
                              :
                              <CurrencyIcon className="h-3 w-3 self-center" />
                              {walletBalance.toFixed(2)}
                            </span>
                          </p>
                          {!walletAvailability.canPay && (
                            <p
                              id="wallet-insufficient-balance"
                              className="text-destructive text-sm"
                            >
                              {t('walletInsufficientBalance', {
                                defaultValue:
                                  'Insufficient balance for this booking',
                              })}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  </label>
                )}

                {/* Apple Pay Option - Only show if available and not hidden */}
                {isApplePayAvailable && !hideCardPayment && (
                  <label
                    data-native-selection-row
                    htmlFor="applepay"
                    className={`flex cursor-pointer items-center rounded-lg border p-4 transition-colors ${
                      selectedMethod === 'applepay'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="applepay" id="applepay" />
                      <div className="flex items-center gap-2">
                        <Image
                          src="/images/payments/apple-pay-mark.svg"
                          alt="Apple Pay"
                          width={48}
                          height={30}
                          className="h-6 w-auto"
                          priority
                        />
                      </div>
                    </div>
                  </label>
                )}

                {/* Card Payment Option - Tap Payments */}
                {!hideCardPayment && (
                  <label
                    data-native-selection-row
                    htmlFor="card"
                    className={`flex cursor-pointer items-center rounded-lg border p-4 transition-colors ${
                      selectedMethod === 'card'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="card" id="card" />
                      <div className="flex items-center gap-2">
                        <CreditCard className="text-primary h-5 w-5 shrink-0" />
                        <div>
                          <p className="font-medium">{t('payWithCard')}</p>
                        </div>
                      </div>
                    </div>
                  </label>
                )}

                {/* Split Payment Option - Hidden for class bookings */}
                {!hideSplitPayment && (
                  <label
                    data-native-selection-row
                    htmlFor="split"
                    className={`flex cursor-pointer items-center rounded-lg border p-4 transition-colors ${
                      selectedMethod === 'split'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="split" id="split" />
                      <div className="flex items-center gap-2">
                        <Users className="text-primary h-5 w-5 shrink-0" />
                        <div>
                          <p className="font-medium">{t('splitWithFriends')}</p>
                          <p className="text-muted-foreground text-sm">
                            {t('splitDescription')}
                          </p>
                        </div>
                      </div>
                    </div>
                  </label>
                )}

                {/* Split Payer Count Selector - Only show when split is selected */}
                {!hideSplitPayment &&
                  selectedMethod === 'split' &&
                  bookingPrice && (
                    <div className="bg-muted/30 rounded-lg border p-4">
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-sm font-medium">
                          {t('numberOfPlayers')}
                        </span>
                        <span className="text-muted-foreground text-sm">
                          <span
                            dir="ltr"
                            className="inline-flex items-center gap-0.5"
                          >
                            <CurrencyIcon className="inline h-3 w-3" />
                            {(splitComp
                              ? splitComp.othersPay
                              : bookingPrice / splitPayerCount
                            ).toFixed(2)}
                          </span>{' '}
                          {t('each')}
                        </span>
                      </div>
                      {splitComp && (
                        <p className="mb-3 text-sm font-medium text-green-700">
                          {t('memberShareCovered', {
                            defaultValue:
                              'Your share is covered by your membership — you pay nothing.',
                          })}
                        </p>
                      )}
                      <div className="flex gap-2">
                        {[4, 3, 2]
                          .filter((c) => !maxSplitPayers || c <= maxSplitPayers)
                          .map((count) => (
                            <Button
                              key={count}
                              type="button"
                              variant={
                                splitPayerCount === count
                                  ? 'default'
                                  : 'outline'
                              }
                              size="sm"
                              className="flex-1"
                              onClick={() => onSplitPayerCountChange?.(count)}
                              disabled={disabled}
                            >
                              {count}
                            </Button>
                          ))}
                      </div>
                    </div>
                  )}

                {/* Cash Payment Option - Last (controlled solely by the tenant's
                    cash setting). Cash is a valid direct payment even when card /
                    Apple Pay are hidden, so it stays independent of hideCardPayment. */}
                {!hideCashPayment && (
                  <label
                    data-native-selection-row
                    htmlFor="cash"
                    className={`flex cursor-pointer items-center rounded-lg border p-4 transition-colors ${
                      selectedMethod === 'cash'
                        ? 'border-primary bg-primary/5'
                        : 'border-border hover:bg-muted/50'
                    } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <RadioGroupItem value="cash" id="cash" />
                      <div className="flex items-center gap-2">
                        <Banknote className="text-primary h-5 w-5 shrink-0" />
                        <div>
                          <p className="font-medium">{t('cashPayAtVenue')}</p>
                        </div>
                      </div>
                    </div>
                  </label>
                )}

                {/* Empty state: card/Apple Pay hidden, no usable package credits,
                    and cash isn't offered either — leaving nothing to pick, so
                    prompt the customer to buy a package (or log in). When cash is
                    available it acts as the fallback and this prompt is suppressed. */}
                {hideCardPayment && !showPackageOption && hideCashPayment && (
                  <div className="rounded-lg border border-dashed p-6 text-center">
                    <Package className="text-muted-foreground mx-auto mb-2 h-8 w-8" />
                    <p className="text-muted-foreground text-sm">
                      {isGuest ? (
                        <Trans
                          i18nKey="bookings:classPackageRequiredOrLogin"
                          components={{
                            loginLink: (
                              <Link
                                href={`/auth/sign-in?next=${typeof window !== 'undefined' ? window.location.pathname : ''}`}
                                className="text-primary font-medium underline"
                              />
                            ),
                          }}
                        />
                      ) : (
                        t('classPackageRequired')
                      )}
                    </p>
                  </div>
                )}
              </div>
            </RadioGroup>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
